import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Loader2, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const partnershipPackages = [
  {
    id: "presenting",
    name: "THE ROOM — PRESENTED BY",
    price: "$100,000+",
    description: "Title partnership of the entire experience"
  },
  {
    id: "main_stage",
    name: "THE MAIN STAGE — POWERED BY",
    price: "$50,000+",
    description: "Ownership of the headline broadcast environment"
  },
  {
    id: "vault",
    name: "THE VAULT — PRESENTED BY",
    price: "$50,000+",
    description: "Association with the legendary prize vault"
  },
  {
    id: "after_dark",
    name: "THE ROOM: AFTER DARK — PRESENTED BY",
    price: "$35,000+",
    description: "Exclusive evening experience partnership"
  },
  {
    id: "stream_floor",
    name: "THE STREAM FLOOR — POWERED BY",
    price: "$25,000+",
    description: "Presenting partner of live commerce broadcasts"
  },
  {
    id: "deal_room",
    name: "PRIVATE DEAL ROOM — PRESENTED BY",
    price: "$15,000–$20,000",
    description: "Named private transaction environment"
  },
  {
    id: "poker_table",
    name: "ALL IN POKER TABLE",
    price: "$10,000",
    description: "Table sponsorship at THE ROOM: ALL IN"
  },
  {
    id: "custom",
    name: "CUSTOM PARTNERSHIP",
    price: "Inquire",
    description: "Build something unique with us"
  }
];

const budgetRanges = [
  "$10,000 – $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000+"
];

const PartnersPage = () => {
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    budget_range: "",
    custom_interest: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const togglePackage = (packageId) => {
    setSelectedPackages(prev => 
      prev.includes(packageId)
        ? prev.filter(p => p !== packageId)
        : [...prev, packageId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedPackages.length === 0) {
      setError("Please select at least one partnership interest");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/partners/inquiry`, {
        ...formData,
        partnership_interest: selectedPackages
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-void flex items-center justify-center px-6">
        <motion.div 
          className="text-center max-w-lg"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 rounded-full border-2 border-gold-500 flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-gold-500" />
          </div>
          <h1 className="font-cinzel text-3xl text-parchment mb-4">Inquiry Received</h1>
          <p className="font-manrope text-stone-400 mb-8">
            Thank you for your interest in partnering with Sovereign. A member of our team will be in touch within 48 hours.
          </p>
          <Link 
            to="/"
            className="font-manrope text-sm text-gold-500 hover:text-gold-400 transition-colors"
          >
            Return to Sovereign
          </Link>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-void">
      {/* Header */}
      <header className="py-6 px-6 border-b border-stone-800">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 font-manrope text-sm text-stone-500 hover:text-parchment transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Sovereign
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-28 border-b border-stone-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <p className="font-manrope text-[11px] tracking-[0.3em] text-gold-500 uppercase mb-6">
              Partnership Opportunities
            </p>
            
            <h1 className="font-cinzel text-4xl md:text-6xl text-parchment mb-8">
              OWN THE ROOM
            </h1>
            
            <div className="max-w-2xl mx-auto">
              <p className="font-cormorant text-xl md:text-2xl text-stone-300 italic mb-6">
                The most valuable partnerships aren't impressions.
              </p>
              <p className="font-cormorant text-xl md:text-2xl text-gold-500 italic">
                They're position.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 border-b border-stone-800">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.p 
            className="font-manrope text-stone-400 leading-relaxed"
            {...fadeUp}
          >
            A select number of companies will be given the opportunity to become part of The Room itself. 
            Not sponsors. Partners. With ownership of moments, environments, and experiences that define 
            what happens when the most serious people in hard assets come together.
          </motion.p>
        </div>
      </section>

      {/* Partnership Packages */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="font-manrope text-[10px] tracking-[0.2em] text-stone-500 uppercase mb-4">
              Select Your Interest
            </p>
            <h2 className="font-cinzel text-3xl text-parchment">
              Partnership Opportunities
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4 mb-16">
            {partnershipPackages.map((pkg, idx) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className={`border p-6 cursor-pointer transition-all duration-300 ${
                  selectedPackages.includes(pkg.id)
                    ? 'border-gold-500 bg-gold-500/5'
                    : 'border-stone-700 hover:border-stone-500'
                }`}
                onClick={() => togglePackage(pkg.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-manrope text-sm text-parchment mb-1">{pkg.name}</h3>
                    <p className="font-cormorant text-xl text-gold-500">{pkg.price}</p>
                    <p className="font-manrope text-xs text-stone-500 mt-2">{pkg.description}</p>
                  </div>
                  <div className={`w-6 h-6 border flex items-center justify-center flex-shrink-0 ml-4 ${
                    selectedPackages.includes(pkg.id)
                      ? 'border-gold-500 bg-gold-500'
                      : 'border-stone-600'
                  }`}>
                    {selectedPackages.includes(pkg.id) && (
                      <Check className="w-4 h-4 text-charcoal" />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Form */}
          <motion.div 
            className="max-w-2xl mx-auto border border-stone-700 p-8 md:p-12"
            {...fadeUp}
          >
            <div className="text-center mb-10">
              <Building2 className="w-10 h-10 text-gold-500 mx-auto mb-4" />
              <h3 className="font-cinzel text-2xl text-parchment mb-2">Build Something With Us</h3>
              <p className="font-manrope text-sm text-stone-500">
                Tell us about your company and partnership goals
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-manrope text-[10px] tracking-[0.15em] text-stone-500 uppercase mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-parchment focus:border-gold-500 focus:outline-none transition-colors"
                    placeholder="Your company"
                  />
                </div>
                <div>
                  <label className="block font-manrope text-[10px] tracking-[0.15em] text-stone-500 uppercase mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-parchment focus:border-gold-500 focus:outline-none transition-colors"
                    placeholder="Your name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-manrope text-[10px] tracking-[0.15em] text-stone-500 uppercase mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-parchment focus:border-gold-500 focus:outline-none transition-colors"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label className="block font-manrope text-[10px] tracking-[0.15em] text-stone-500 uppercase mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-parchment focus:border-gold-500 focus:outline-none transition-colors"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              <div>
                <label className="block font-manrope text-[10px] tracking-[0.15em] text-stone-500 uppercase mb-2">
                  Budget Range
                </label>
                <select
                  value={formData.budget_range}
                  onChange={(e) => setFormData({...formData, budget_range: e.target.value})}
                  className="w-full bg-void border border-stone-700 px-4 py-3 font-manrope text-parchment focus:border-gold-500 focus:outline-none transition-colors"
                >
                  <option value="">Select range</option>
                  {budgetRanges.map(range => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
              </div>

              {selectedPackages.includes("custom") && (
                <div>
                  <label className="block font-manrope text-[10px] tracking-[0.15em] text-stone-500 uppercase mb-2">
                    Custom Partnership Idea
                  </label>
                  <textarea
                    value={formData.custom_interest}
                    onChange={(e) => setFormData({...formData, custom_interest: e.target.value})}
                    rows={3}
                    className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-parchment focus:border-gold-500 focus:outline-none transition-colors resize-none"
                    placeholder="Describe your custom partnership vision..."
                  />
                </div>
              )}

              <div>
                <label className="block font-manrope text-[10px] tracking-[0.15em] text-stone-500 uppercase mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-parchment focus:border-gold-500 focus:outline-none transition-colors resize-none"
                  placeholder="Anything else we should know..."
                />
              </div>

              {error && (
                <div className="p-4 border border-red-500/30 bg-red-500/10">
                  <p className="font-manrope text-sm text-red-400 text-center">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gold-500 text-charcoal font-manrope text-sm tracking-[0.15em] uppercase hover:bg-gold-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Inquiry"
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-stone-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-manrope text-xs text-stone-600">
            For immediate inquiries: <a href="mailto:partners@sovereigngroup.io" className="text-stone-500 hover:text-gold-500 transition-colors">partners@sovereigngroup.io</a>
          </p>
        </div>
      </footer>
    </main>
  );
};

export default PartnersPage;
